import { useQuery } from '@apollo/client';
import Fuse, { type IFuseOptions } from 'fuse.js';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Body1,
    Flex,
    Heading1,
    HorizontalDivider,
    IconButton,
    IconInfo,
    LoadingPlaceholder,
    Modal,
    OutlinedCard,
    SearchInput,
    Stack,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import {
    type DigitallyReceivedPrescriberOrderBasicInfoFragment,
    GetBasicPrescriberOrdersDocument,
    PrescriberOrderStatus,
} from '../../../graphql/generated/graphql';
import { usePatientContext } from '../../../hooks/use-patient-context';
import { sortOrdersByDate } from '../../../util';
import { ConditionalComponent } from '../../utility/ConditionalComponent';

import { HistorySearchItem } from './history/HistorySearchItem';
import { NoHistory } from './history/NoHistory';
import { NoMatch } from './history/NoMatch';

type SearchablePrescribedMedication =
    DigitallyReceivedPrescriberOrderBasicInfoFragment & {
        patientFirstName?: string;
        patientLastName?: string;
        formCode: string;
        strengthUnit: string;
    };

const fuseOptions: IFuseOptions<SearchablePrescribedMedication> = {
    keys: [
        { name: 'productDescription', weight: 2 },
        'formCode',
        'strengthUnit',
    ],
    threshold: 0.1,
};

export const MedicineCabinetHistoryPage = () => {
    const { t } = useTranslation();
    const { patient } = usePatientContext();
    const [isModalOpen, { open: openModal, close: closeModal }] =
        useDisclosure();
    const [search, setSearch] = useState('');

    const { data, loading } = useQuery(GetBasicPrescriberOrdersDocument, {
        errorPolicy: 'all',
        variables: {
            patientId: patient?.id ?? '',
            input: {
                statusFilter: [
                    PrescriberOrderStatus.Filled,
                    PrescriberOrderStatus.SentToPharmacy,
                    PrescriberOrderStatus.Cancelled,
                ],
            },
        },
    });

    const prescriptions = useMemo(() => {
        if (!data) return [];

        const primaryPrescriptions =
            data?.patient?.prescriberOrdersConnection.edges.map((edge) =>
                getFragment(getFragment(edge.node))
            ) ?? [];

        const consentedPrescriptions =
            data?.patient?.consentedPatientsConnection.edges
                .map((edge) =>
                    edge.node.prescriberOrdersConnection.edges.map((edge) =>
                        getFragment(getFragment(edge.node))
                    )
                )
                .flat() ?? [];

        return [...primaryPrescriptions, ...consentedPrescriptions].filter(
            (p) =>
                p.__typename === 'DigitallyReceivedPrescriberOrder' &&
                p.status !== PrescriberOrderStatus.Pending &&
                p.status !== PrescriberOrderStatus.New &&
                patient?.id
        );
    }, [data, patient]);

    const searchablePrescribedMedications = useMemo<
        SearchablePrescribedMedication[]
    >(() => {
        return prescriptions.map((p) => {
            const med = getFragment(p.authorizedMedication);
            return {
                ...p,
                formCode:
                    med.__typename === 'AuthorizedMedication'
                        ? med.formCode
                        : '',
                strengthUnit:
                    med.__typename === 'AuthorizedMedication'
                        ? med.strengthUnit
                        : '',
            };
        });
    }, [prescriptions]);

    const filtered = useMemo(() => {
        if (!search) {
            return sortOrdersByDate(
                searchablePrescribedMedications,
                'assignmentTimestamp'
            );
        }

        const fuse = new Fuse(searchablePrescribedMedications, fuseOptions);
        return sortOrdersByDate(
            fuse.search(search).map((f) => f.item),
            'assignmentTimestamp'
        );
    }, [searchablePrescribedMedications, search]);

    return (
        <Stack gap="sm">
            <Flex align="center" gap="xxs">
                <Heading1>{t('cabinet.history.heading')}</Heading1>
                <IconButton
                    Icon={IconInfo}
                    onClick={openModal}
                    withStyles={false}
                />
                <Modal
                    opened={isModalOpen}
                    title={t('cabinet.history.infoModalHeader')}
                    onClose={closeModal}
                >
                    <Body1>{t('cabinet.history.infoModalBody')}</Body1>
                </Modal>
            </Flex>

            <Stack gap="lg">
                <SearchInput
                    label={t('cabinet.history.searchInput.label')}
                    onChange={(e) => setSearch(e.target.value)}
                    value={search}
                    clear={() => setSearch('')}
                />

                {loading ? (
                    <OutlinedCard>
                        <LoadingPlaceholder
                            rows={3}
                            rowWidth="100%"
                            rowHeight={50}
                            gap="lg"
                        />
                    </OutlinedCard>
                ) : (
                    <>
                        <ConditionalComponent condition={filtered.length > 0}>
                            <OutlinedCard>
                                <Stack gap="sm">
                                    {filtered.map((p, i) => (
                                        <Fragment key={p.id}>
                                            {i > 0 && <HorizontalDivider />}
                                            <HistorySearchItem
                                                item={
                                                    p as DigitallyReceivedPrescriberOrderBasicInfoFragment
                                                }
                                            />
                                        </Fragment>
                                    ))}
                                </Stack>
                            </OutlinedCard>
                        </ConditionalComponent>

                        <ConditionalComponent
                            condition={
                                !!prescriptions.length && !filtered.length
                            }
                        >
                            <NoMatch />
                        </ConditionalComponent>

                        <ConditionalComponent
                            condition={
                                !prescriptions.length && !filtered.length
                            }
                        >
                            <NoHistory />
                        </ConditionalComponent>
                    </>
                )}
            </Stack>
        </Stack>
    );
};
